import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, Zap, ListTodo, Telescope, ChevronRight, ShieldCheck } from "lucide-react";

/**
 * /roadmap — public roadmap.
 *
 * Three columns: Now / Next / Later. Subtle navy/slate, sharp edges.
 * Items here are directional, not commitments — phrased that way explicitly to
 * stay on the right side of FCA financial-promotion guidance and SaaS norms.
 */
const NOW = [
  { title: "Native HubSpot delivery preset",       body: "One-click HubSpot CRM payload mapping on top of your existing webhook delivery — no OAuth dance required.", tag: "Q2 2026" },
  { title: "Per-campaign daily caps",              body: "Independent daily caps per campaign (today caps are account-wide). Useful for multi-sector buyers.",         tag: "Q2 2026" },
  { title: "Granular publisher reporting export",  body: "CSV export of publisher earnings, payouts and dispute history for finance team reconciliation.",            tag: "Q2 2026" },
];

const NEXT = [
  { title: "Native Salesforce + Pipedrive presets",      body: "Same as HubSpot — pre-mapped JSON payload shapes for the two other major UK CRMs.",                       tag: "Q3 2026" },
  { title: "Multi-sector campaigns",                     body: "Single buyer can run one campaign that matches multiple sectors with independent budgets and caps.",       tag: "Q3 2026" },
  { title: "Audit log export to S3 / customer bucket",   body: "Continuous audit-trail replication to a customer-owned S3 bucket — for buyers with internal SIEM needs.",  tag: "Q3 2026" },
  { title: "Sandbox lead replay tool",                   body: "Replay a real lead through your routing config in dry-run mode to validate ping-tree changes before live.", tag: "Q3 2026" },
];

const LATER = [
  { title: "Custom routing logic (JS sandbox)",     body: "Bring-your-own routing rules — admin writes a JS function that runs in a v8 isolate per lead.",      tag: "H2 2026" },
  { title: "Real-time inbound voice routing",       body: "Inbound call → Connexis IVR → matched to the highest-priority eligible buyer (voice equivalent of lead routing).", tag: "H2 2026" },
  { title: "Embedded reporting iframe for buyers",  body: "White-labelled reporting widget buyers can embed in their own admin tools — same auth, no Connexis branding.",  tag: "H2 2026" },
  { title: "EU + US data residency",                body: "Dedicated EU and US regions. Customer choice at signup. All data + audit trail co-located.",                     tag: "2027" },
];

const SHIPPED = [
  "CPL waterfall on ping trees",
  "Buyer business hours + Campaign dashboard",
  "Sandbox API key for publishers",
  "Developer documentation rebuild",
  "Admin Action Centre + daily digest",
  "Bulk Nudge-all for contracts & invoices",
  "Manual contract issuance",
  "Stripe Connect for affiliate payouts",
  "Auto Follow-Up (SMS + email)",
  "AI Reply Triage — intent classification",
];

export default function Roadmap() {
  return (
    <div className="bg-white min-h-screen" data-testid="roadmap-page">

      {/* Hero */}
      <section className="bg-slate-950 text-white border-b border-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_-10%,rgba(34,211,166,0.10),transparent_55%)] pointer-events-none" />
        <div className="max-w-5xl mx-auto px-6 py-20 relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm border border-white/10 bg-white/5 text-[10.5px] uppercase tracking-[0.18em] text-white/65 font-mono mb-7">
            <Telescope className="size-3" /> Roadmap
          </div>
          <h1 className="text-4xl sm:text-5xl tracking-tight font-semibold leading-[1.05] max-w-3xl">
            What we're building next.<br />
            <span className="text-white/55">In public.</span>
          </h1>
          <p className="mt-6 text-[15px] text-white/60 max-w-xl leading-relaxed">
            Directional, not committal. We ship continuously, so timelines move. The further right a column, the more likely the scope shifts based on customer signal.
          </p>
          <div className="mt-8 flex items-center gap-3 flex-wrap">
            <Button asChild variant="outline" className="rounded-full border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-white">
              <Link to="/changelog" data-testid="roadmap-changelog-cta">View changelog</Link>
            </Button>
            <Button asChild className="rounded-full bg-white text-slate-950 hover:bg-white/90">
              <Link to="/book-demo" data-testid="roadmap-demo-cta">Request a feature <ArrowUpRight className="size-4 ml-1" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Three columns */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
          <Column label="Now"   sub="In active development"  Icon={Zap}     items={NOW}   accent="bg-[#0055FF]" testid="roadmap-now" />
          <Column label="Next"  sub="Scoped, queued"         Icon={ListTodo} items={NEXT}  accent="bg-slate-700"  testid="roadmap-next" />
          <Column label="Later" sub="Directional"            Icon={Telescope} items={LATER} accent="bg-slate-400"  testid="roadmap-later" />
        </div>
      </section>

      {/* Already shipped */}
      <section className="border-t border-slate-100 bg-slate-50/60">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="inline-flex items-center gap-2.5 mb-5">
            <ShieldCheck className="size-4 text-emerald-700" />
            <span className="text-[11px] uppercase tracking-[0.18em] text-emerald-800 font-mono font-semibold">Already shipped</span>
          </div>
          <h2 className="text-2xl sm:text-3xl tracking-tight font-semibold text-slate-900 leading-tight max-w-2xl">
            What's already in production today.
          </h2>
          <div className="mt-8 flex flex-wrap gap-2">
            {SHIPPED.map((s) => (
              <span key={s} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-slate-200 bg-white text-[13px] text-slate-700">
                <span className="size-1.5 rounded-full bg-emerald-500" />
                {s}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold text-slate-900 max-w-xl mx-auto leading-tight">
            Missing something you need?
          </h2>
          <p className="mt-4 text-[15px] text-slate-600 max-w-lg mx-auto">
            We prioritise heavily on customer signal. Tell us what you're trying to build and we'll tell you honestly whether we'll ship it.
          </p>
          <div className="mt-7">
            <Button asChild className="rounded-full bg-slate-900 hover:bg-slate-800">
              <Link to="/book-demo" data-testid="roadmap-bottom-cta">Talk to product <ArrowUpRight className="size-4 ml-1" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

function Column({ label, sub, Icon, items, accent, testid }) {
  return (
    <div className="bg-white" data-testid={testid}>
      <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
        <span className={`inline-flex size-8 items-center justify-center rounded-sm ${accent} text-white`}>
          <Icon className="size-4" strokeWidth={1.6} />
        </span>
        <div>
          <div className="text-sm font-semibold text-slate-900 tracking-tight">{label}</div>
          <div className="text-[11.5px] uppercase tracking-[0.14em] text-slate-500 font-mono">{sub}</div>
        </div>
      </div>
      <ul className="divide-y divide-slate-100">
        {items.map((it, i) => (
          <li key={i} className="px-6 py-5 hover:bg-slate-50/60 transition-colors">
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-[14px] font-semibold text-slate-900 leading-tight">{it.title}</h3>
              <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-mono shrink-0 mt-0.5">{it.tag}</span>
            </div>
            <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{it.body}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}
